import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, INR, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader } from "@/components/shared";
import { QualityBadge } from "@/components/QualityBadge";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Headphones, Phone, MessageCircle, PhoneCall, Loader2, AlarmClock, FileCheck2 } from "lucide-react";
import { useCallTracker } from "@/components/CallTracker";
import { DatePicker, DateTimePicker } from "@/components/DatePicker";

const INS_STATUS = ["contacted", "interested", "follow_up", "quote_generated", "not_interested", "not_reachable", "wrong_number"];
const LOAN_STATUS = ["contacted", "interested", "documents_pending", "documents_received", "submitted", "not_interested", "closed"];
const DOC_ST = ["not_requested", "pending", "uploaded", "under_review", "accepted", "rejected"];

function CallDialog({ lead, outcomes, onClose, onSaved }) {
  const { hasPerm } = useAuth();
  const canReview = hasPerm("loans:edit") || hasPerm("*");
  const [v, setV] = useState({ outcome: "", remarks: "", next_follow_up: "", status: "" });
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const leadId = lead?.id;
  useEffect(() => { if (leadId) api.get("/workspace/calls", { params: { lead_id: leadId } }).then(({ data }) => setHist(data)).catch(() => setHist([])); setV({ outcome: "", remarks: "", next_follow_up: "", status: "" }); }, [leadId]);
  if (!lead) return null;
  const statuses = lead.business === "loan" ? LOAN_STATUS : INS_STATUS;
  const save = async () => {
    setBusy(true);
    try {
      const nfu = v.next_follow_up ? new Date(v.next_follow_up).toISOString() : null;
      await api.post("/workspace/call", { business: lead.business, lead_id: lead.id, ...v, next_follow_up: nfu, status: v.status || null });
      toast.success("Call logged"); onSaved();
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setDoc = async (d, status) => {
    let reason = null;
    if (status === "rejected") { reason = window.prompt(`Why is ${d.name} rejected?`); if (!reason) return; }
    try { await api.put("/workspace/docs", { business: lead.business, lead_id: lead.id, name: d.name, status, reason }); toast.success("Document updated"); onSaved(true); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="call-dialog">
        <DialogHeader><DialogTitle>{lead.name} · <span className="font-mono text-sm">{lead.mobile}</span></DialogTitle></DialogHeader>
        <QualityBadge quality={lead.quality} showReasons />
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <div><Label className="text-xs">Call result *</Label>
            <Select value={v.outcome} onValueChange={(o) => setV({ ...v, outcome: o })}><SelectTrigger className="mt-1" data-testid="call-outcome-select"><SelectValue placeholder="Pick result" /></SelectTrigger>
              <SelectContent>{outcomes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Next follow-up</Label><DateTimePicker className="mt-1" value={v.next_follow_up} onChange={(x) => setV({ ...v, next_follow_up: x })} testId="call-followup-input" /></div>
          <div><Label className="text-xs">Update status (optional)</Label>
            <Select value={v.status} onValueChange={(s) => setV({ ...v, status: s })}><SelectTrigger className="mt-1" data-testid="call-status-select"><SelectValue placeholder={titleCase(lead.status)} /></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select></div>
          <div className="sm:col-span-2"><Label className="text-xs">Remarks</Label><Textarea className="mt-1" value={v.remarks} onChange={(e) => setV({ ...v, remarks: e.target.value })} data-testid="call-remarks-input" /></div>
        </div>
        {lead.doc_checklist.length > 0 && (
          <div className="mt-2">
            <div className="text-sm font-semibold mb-1 flex items-center gap-1.5"><FileCheck2 className="h-4 w-4" />Documents to collect</div>
            {lead.doc_checklist.map((d) => (
              <div key={d.name} className="flex items-center justify-between gap-2 py-1.5 border-b border-border text-sm" data-testid="ws-doc-row">
                <span>{d.name}{d.required ? " *" : ""}{d.reason && <span className="text-xs text-rose-600 ml-2">({d.reason})</span>}</span>
                <Select value={d.status} onValueChange={(s) => setDoc(d, s)}><SelectTrigger className="w-40 h-8" data-testid="ws-doc-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_ST.filter((s) => canReview || !["accepted", "rejected"].includes(s) || s === d.status).map((s) => <SelectItem key={s} value={s} disabled={!canReview && ["accepted", "rejected"].includes(s)}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
              </div>
            ))}
          </div>
        )}
        {hist.length > 0 && (
          <div className="mt-2 text-sm"><div className="font-semibold mb-1">Call history</div>
            {hist.slice(0, 5).map((h) => <div key={h.id} className="text-xs text-muted-foreground py-0.5" data-testid="ws-call-history-row">{fmtDateTime(h.at)} · {h.user_name} · <b>{h.outcome}</b>{h.remarks ? ` — ${h.remarks}` : ""}</div>)}
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={busy || !v.outcome} data-testid="call-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save call"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadCard({ l, onCall }) {
  const { trackCall } = useCallTracker();
  const overdue = l.next_follow_up && new Date(l.next_follow_up) < new Date();
  return (
    <div className={`rounded-xl border bg-card p-4 flex flex-col gap-3 ${overdue ? "border-rose-300 dark:border-rose-800" : "border-border"}`} data-testid="ws-lead-card">
      <div className="flex justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold truncate">{l.name}</div>
          <div className="text-xs text-muted-foreground">{l.ref} · {titleCase(l.business)}{l.product ? ` · ${l.product}` : ""}{l.amount ? ` · ${INR(l.amount)}` : ""}{l.city ? ` · ${l.city}` : ""}</div>
        </div>
        <StatusBadge value={l.status} />
      </div>
      <QualityBadge quality={l.quality} />
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
        <span className={overdue ? "text-rose-600 font-semibold" : ""}><AlarmClock className="h-3.5 w-3.5 inline mr-1" />{l.next_follow_up ? fmtDateTime(l.next_follow_up) : "No follow-up set"}</span>
        <span>{l.call_count} calls{l.last_outcome ? ` · last: ${l.last_outcome}` : ""}</span>
        {l.preferred_callback_time && <span>Prefers {l.preferred_callback_time}</span>}
      </div>
      <div className="flex gap-2">
        <Button asChild size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="ws-call-btn"><a href={`tel:${l.mobile}`} onClick={() => trackCall({ business: l.business, lead_id: l.id, name: l.name, mobile: l.mobile })}><Phone className="h-4 w-4 mr-1" />Call</a></Button>
        <WhatsAppButton lead={l} size="sm" variant="outline" className="" testId="ws-whatsapp-btn" />
        <Button size="sm" variant="outline" className="flex-1" onClick={() => onCall(l)} data-testid="ws-log-call-btn"><PhoneCall className="h-4 w-4 mr-1" />Log result</Button>
      </div>
    </div>
  );
}

function TargetRing({ t }) {
  const r = 26, c = 2 * Math.PI * r, pct = Math.min(100, t.percent);
  const color = pct >= 100 ? "#059669" : pct >= 50 ? "#2563eb" : "#f59e0b";
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2" data-testid="call-target-ring">
      <svg width="64" height="64" viewBox="0 0 64 64" className="-rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="currentColor" strokeWidth="6" className="text-muted" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} style={{ transition: "stroke-dashoffset 600ms ease" }} />
      </svg>
      <div>
        <div className="text-lg font-bold tabular-nums leading-tight" data-testid="call-target-count">{t.done}<span className="text-muted-foreground text-sm font-medium"> / {t.target}</span></div>
        <div className="text-[11px] text-muted-foreground">calls today · {t.connected} connected</div>
      </div>
    </div>
  );
}

export default function Workspace() {
  const [target, setTarget] = useState(null);
  const loadTarget = useCallback(() => api.get("/workspace/target").then(({ data }) => setTarget(data)).catch(() => {}), []);
  useEffect(() => { loadTarget(); }, [loadTarget]);
  const [scope, setScope] = useState("mine");
  const [bucket, setBucket] = useState("all");
  const [business, setBusiness] = useState("all");
  const [data, setData] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const [active, setActive] = useState(null);
  const load = useCallback(() => api.get("/workspace/queue", { params: { scope, bucket, business } }).then(({ data }) => { setData(data); return data; }), [scope, bucket, business]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/workspace/outcomes").then(({ data }) => setOutcomes(data)); }, []);
  useEffect(() => { const r = () => { load(); loadTarget(); }; window.addEventListener("omi-call-logged", r); return () => window.removeEventListener("omi-call-logged", r); }, [load, loadTarget]);
  const onSaved = async (keepOpen) => {
    const d = await load();
    loadTarget();
    if (keepOpen && active) setActive(d.items.find((i) => i.id === active.id) || null); else setActive(null);
  };
  return (
    <div>
      <PageHeader title="Calling Workspace" subtitle="Overdue follow-ups first, then highest quality score" icon={Headphones}
        actions={target && <TargetRing t={target} />} />
      <div className="flex flex-wrap gap-3 mb-4">
        {data?.can_view_all && (
          <Select value={scope} onValueChange={setScope}><SelectTrigger className="w-44" data-testid="ws-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="mine">My leads</SelectItem><SelectItem value="all">All leads</SelectItem><SelectItem value="unassigned">Unassigned</SelectItem></SelectContent></Select>
        )}
        <Select value={bucket} onValueChange={setBucket}><SelectTrigger className="w-44" data-testid="ws-bucket-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All open</SelectItem><SelectItem value="new">Not called yet</SelectItem><SelectItem value="today">Follow-up today</SelectItem><SelectItem value="overdue">Overdue</SelectItem></SelectContent></Select>
        <Select value={business} onValueChange={setBusiness}><SelectTrigger className="w-40" data-testid="ws-business-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Insurance + Loan</SelectItem><SelectItem value="insurance">Insurance</SelectItem><SelectItem value="loan">Loan</SelectItem></SelectContent></Select>
        {data && <span className="text-sm text-muted-foreground self-center" data-testid="ws-counts">{data.counts.total} leads · <span className="text-rose-600 font-semibold">{data.counts.overdue} overdue</span></span>}
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin mx-auto mt-16 text-muted-foreground" /> : data.items.length === 0 ? (
        <p className="text-center text-muted-foreground mt-16" data-testid="ws-empty">Nothing in this queue right now.</p>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{data.items.map((l) => <LeadCard key={l.id} l={l} onCall={setActive} />)}</div>
      )}
      <CallDialog lead={active} outcomes={outcomes} onClose={() => setActive(null)} onSaved={onSaved} />
    </div>
  );
}
